import { z } from 'zod';

import { ApiResponseError, assertOk, parseJson } from './response';

export interface ReportsSummary {
  total_revenue_cents: number;
  orders: number;
  avg_order_value_cents: number;
}

export interface ReportsSalesByDay {
  date: string;
  revenue_cents: number;
  orders: number;
}

export interface ReportsTopItem {
  item_id: string;
  name: string;
  qty: number;
  revenue_cents: number;
}

export interface ReportsTenders {
  cash_cents?: number;
  card_cents?: number;
  tips_cents?: number;
  refunds_cents?: number;
}

export interface ReportsZSummary {
  opening_float_cents?: number;
  cash_sales_cents?: number;
  cash_refunds_cents?: number;
  drops_cents?: number;
  payouts_cents?: number;
  closing_cash_cents?: number;
  gross_sales_cents?: number;
  net_sales_cents?: number;
  discount_cents?: number;
  tax_cents?: number;
  service_charge_cents?: number;
  surcharge_cents?: number;
}

export interface ReportsSalesTotals {
  subtotal_cents?: number;
  discount_cents?: number;
  net_sales_cents?: number;
  tax_cents?: number;
  service_charge_cents?: number;
  surcharge_cents?: number;
  tips_cents?: number;
}

export interface ReportsDaypartEntry {
  key: string;
  label?: string | null;
  start_minute?: number | null;
  end_minute?: number | null;
  total_revenue_cents?: number | null;
  orders?: number | null;
}

export interface ReportsChannelEntry {
  key?: string | null;
  label?: string | null;
  total_revenue_cents?: number | null;
  orders?: number | null;
}

export interface ReportsPeriodMetric {
  current: number;
  previous: number;
  delta: number;
  delta_percent: number | null;
}

export interface ReportsPeriodComparison {
  previous_range: {
    from: string;
    to: string;
  };
  metrics: Record<string, ReportsPeriodMetric>;
}

export interface ReportsSameStoreEntry {
  location_id: string;
  location_name: string;
  revenue_cents: ReportsPeriodMetric;
  orders: ReportsPeriodMetric;
  avg_order_value_cents: ReportsPeriodMetric;
}

export interface ReportsCategoryMixEntry {
  category_id?: string | null;
  category_name: string;
  quantity?: number | null;
  revenue_cents?: number | null;
  share_ratio?: number | null;
  margin_cents?: number | null;
  margin_rate?: number | null;
  avg_prep_minutes?: number | null;
}

export interface ReportsModifierMixEntry {
  modifier_name: string;
  attach_count?: number | null;
  revenue_cents?: number | null;
  margin_cents?: number | null;
  margin_rate?: number | null;
  attach_rate?: number | null;
  share_ratio?: number | null;
}

export interface ReportsComboMixEntry {
  combo_name: string;
  quantity?: number | null;
  revenue_cents?: number | null;
  margin_cents?: number | null;
  margin_rate?: number | null;
}

export interface ReportsCategoryRecommendationEntry {
  category_id?: string | null;
  category_name: string;
  rank: number;
  reason: string;
}

export interface ReportsCategoryRecommendations {
  experiment_key?: string | null;
  variant?: string | null;
  strategy: 'default' | 'popularity' | 'prep_speed';
  categories: ReportsCategoryRecommendationEntry[];
}

export interface ReportsLocationMarginEntry {
  location_id: string;
  location_name: string;
  gross_revenue_cents?: number | null;
  net_revenue_cents?: number | null;
  orders?: number | null;
  margin_cents?: number | null;
  margin_rate?: number | null;
}

export interface ReportsRefundActivityEntry {
  payment_id: string;
  order_code?: string | null;
  order_id?: string | null;
  method?: string | null;
  amount_cents: number;
  processed_at: number;
  reason?: string | null;
}

export interface ReportsInventoryInsights {
  estimated_cogs_cents?: number;
  estimated_margin_cents?: number;
  waste?: {
    events?: number;
    quantity?: number;
    estimated_cost_cents?: number;
    top_reasons?: Array<{ reason: string; count: number }>;
    top_items?: Array<{
      name: string;
      location_name?: string | null;
      quantity: number;
      estimated_cost_cents: number;
    }>;
  };
  prep?: {
    events?: number;
    quantity?: number;
    estimated_cost_cents?: number;
    avg_batch_quantity?: number | null;
    top_items?: Array<{
      name: string;
      location_name?: string | null;
      quantity: number;
      estimated_cost_cents: number;
    }>;
    recent_batches?: Array<{
      id: string;
      name: string;
      location_name?: string | null;
      quantity: number;
      estimated_cost_cents: number;
      reference?: string | null;
      occurred_at: number | null;
    }>;
    cadence?: {
      daily?: Array<{
        date: string;
        events: number;
        total_quantity: number;
      }>;
      hourly?: Array<{
        hour: number;
        events: number;
        total_quantity: number;
      }>;
    };
  };
  usage_variance?: {
    total_variance_quantity?: number;
    estimated_variance_cost_cents?: number;
    products?: Array<{
      product_id: string | null;
      product_name: string;
      location_id: string | null;
      location_name: string | null;
      sale_quantity: number;
      other_quantity: number;
      variance_quantity: number;
      estimated_variance_cost_cents: number;
    }>;
  };
  par_breaches?: Array<{
    level_id: string;
    product_id: string | null;
    product_name: string;
    location_id: string | null;
    location_name: string | null;
    on_hand: number;
    reorder_point: number | null;
    deficit: number;
    coverage_days: number | null;
    severity: 'critical' | 'warning';
    updated_at: number | null;
  }>;
  low_stock?: Array<{
    level_id: string;
    product_id: string | null;
    product_name: string;
    location_id: string | null;
    location_name: string | null;
    on_hand: number;
    reorder_point: number | null;
    daily_burn_rate: number | null;
    updated_at: number | null;
  }>;
  depletion_risks?: Array<{
    level_id: string;
    product_id: string | null;
    product_name: string;
    location_id: string | null;
    location_name: string | null;
    on_hand: number;
    reorder_point: number | null;
    daily_burn_rate: number | null;
    updated_at: number | null;
  }>;
}

export interface ReportsLaborInsights {
  regular_hours: number;
  overtime_hours: number;
  premium_hours: number;
  total_hours: number;
  overtime_share: number | null;
  estimated_labor_cost_cents?: number;
  labor_percent: number | null;
  total_tips_cents?: number;
  open_exceptions?: number | null;
  gratuity_by_role?: Array<{
    role: string;
    tips_cents: number;
    percentage: number | null;
  }>;
}

export interface ReportsCateringSnapshot {
  totals: {
    events: number;
    planning: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    pipelineValueCents: number;
    confirmedValueCents: number;
    completedValueCents: number;
    conversionRate: number | null;
    averageGuestCount: number | null;
  };
  payments: {
    outstandingCents: number;
    collectedCents: number;
    dueSoon: number;
    overdue: number;
  };
  upcoming: Array<{
    id: string;
    name: string;
    status: string;
    eventDate: number;
    budgetCents: number | null;
    dueStatus: 'dueSoon' | 'overdue' | null;
  }>;
  currencyMix: Array<{
    currency: string;
    valueCents: number;
    share: number;
  }>;
}


type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

const ReportsSummarySchema = z
  .object({
    total_revenue_cents: z.number(),
    orders: z.number(),
    avg_order_value_cents: z.number(),
  })
  .passthrough();

const ReportsSalesByDaySchema = z
  .object({
    date: z.string(),
    revenue_cents: z.number(),
    orders: z.number(),
  })
  .passthrough();

const ReportsTopItemSchema = z
  .object({
    item_id: z.string(),
    name: z.string(),
    qty: z.number(),
    revenue_cents: z.number(),
  })
  .passthrough();

const ReportsTendersSchema = z
  .object({
    cash_cents: z.number().optional(),
    card_cents: z.number().optional(),
    tips_cents: z.number().optional(),
    refunds_cents: z.number().optional(),
  })
  .passthrough();

const ReportsZSummarySchema = z
  .object({
    opening_float_cents: z.number().optional(),
    cash_sales_cents: z.number().optional(),
    cash_refunds_cents: z.number().optional(),
    drops_cents: z.number().optional(),
    payouts_cents: z.number().optional(),
    closing_cash_cents: z.number().optional(),
    gross_sales_cents: z.number().optional(),
    net_sales_cents: z.number().optional(),
    discount_cents: z.number().optional(),
    tax_cents: z.number().optional(),
    service_charge_cents: z.number().optional(),
    surcharge_cents: z.number().optional(),
  })
  .passthrough();

const ReportsSalesTotalsSchema = z
  .object({
    subtotal_cents: z.number().optional(),
    discount_cents: z.number().optional(),
    net_sales_cents: z.number().optional(),
    tax_cents: z.number().optional(),
    service_charge_cents: z.number().optional(),
    surcharge_cents: z.number().optional(),
    tips_cents: z.number().optional(),
  })
  .passthrough();

const ReportsDaypartEntrySchema = z
  .object({
    key: z.string(),
    label: z.string().nullable().optional(),
    start_minute: z.number().nullable().optional(),
    end_minute: z.number().nullable().optional(),
    total_revenue_cents: z.number().nullable().optional(),
    orders: z.number().nullable().optional(),
  })
  .passthrough();

const ReportsChannelEntrySchema = z
  .object({
    key: z.string().nullable().optional(),
    label: z.string().nullable().optional(),
    total_revenue_cents: z.number().nullable().optional(),
    orders: z.number().nullable().optional(),
  })
  .passthrough();

const ReportsPeriodMetricSchema = z
  .object({
    current: z.number(),
    previous: z.number(),
    delta: z.number(),
    delta_percent: z.number().nullable(),
  })
  .passthrough();

const ReportsPeriodComparisonSchema = z
  .object({
    previous_range: z
      .object({
        from: z.string().nullable().optional(),
        to: z.string().nullable().optional(),
      })
      .passthrough(),
    metrics: z.record(z.string(), ReportsPeriodMetricSchema),
  })
  .passthrough();

const ReportsSameStoreEntrySchema = z
  .object({
    location_id: z.string(),
    location_name: z.string(),
    revenue_cents: ReportsPeriodMetricSchema,
    orders: ReportsPeriodMetricSchema,
    avg_order_value_cents: ReportsPeriodMetricSchema,
  })
  .passthrough();

const ReportsCategoryMixEntrySchema = z
  .object({
    category_id: z.string().nullable().optional(),
    category_name: z.string(),
    quantity: z.number().nullable().optional(),
    revenue_cents: z.number().nullable().optional(),
    share_ratio: z.number().nullable().optional(),
    margin_cents: z.number().nullable().optional(),
    margin_rate: z.number().nullable().optional(),
    avg_prep_minutes: z.number().nullable().optional(),
  })
  .passthrough();

const ReportsCategoryRecommendationEntrySchema = z
  .object({
    category_id: z.string().nullable().optional(),
    category_name: z.string(),
    rank: z.number(),
    reason: z.string(),
  })
  .passthrough();

const ReportsCategoryRecommendationsSchema = z
  .object({
    experiment_key: z.string().nullable().optional(),
    variant: z.string().nullable().optional(),
    strategy: z.enum(['default', 'popularity', 'prep_speed']),
    categories: z.array(ReportsCategoryRecommendationEntrySchema),
  })
  .passthrough();

const ReportsModifierMixEntrySchema = z
  .object({
    modifier_name: z.string(),
    attach_count: z.number().nullable().optional(),
    revenue_cents: z.number().nullable().optional(),
    margin_cents: z.number().nullable().optional(),
    margin_rate: z.number().nullable().optional(),
    attach_rate: z.number().nullable().optional(),
    share_ratio: z.number().nullable().optional(),
  })
  .passthrough();

const ReportsComboMixEntrySchema = z
  .object({
    combo_name: z.string(),
    quantity: z.number().nullable().optional(),
    revenue_cents: z.number().nullable().optional(),
    margin_cents: z.number().nullable().optional(),
    margin_rate: z.number().nullable().optional(),
  })
  .passthrough();

const ReportsLocationMarginEntrySchema = z
  .object({
    location_id: z.string(),
    location_name: z.string(),
    gross_revenue_cents: z.number().nullable().optional(),
    net_revenue_cents: z.number().nullable().optional(),
    orders: z.number().nullable().optional(),
    margin_cents: z.number().nullable().optional(),
    margin_rate: z.number().nullable().optional(),
  })
  .passthrough();

const ReportsRefundActivityEntrySchema = z
  .object({
    payment_id: z.string(),
    order_code: z.string().nullable().optional(),
    order_id: z.string().nullable().optional(),
    method: z.string().nullable().optional(),
    amount_cents: z.number(),
    processed_at: z.number(),
    reason: z.string().nullable().optional(),
  })
  .passthrough();

const ReportsResponseSchema = z
  .object({
    range: z
      .object({
        from: z.string().nullable().optional(),
        to: z.string().nullable().optional(),
      })
      .passthrough(),
    summary: ReportsSummarySchema,
    sales_by_day: z.array(ReportsSalesByDaySchema),
    top_items: z.array(ReportsTopItemSchema),
    tenders: ReportsTendersSchema.nullable().optional(),
    z_report: ReportsZSummarySchema.nullable().optional(),
    sales_totals: ReportsSalesTotalsSchema.nullable().optional(),
    daypart_breakdown: z.array(ReportsDaypartEntrySchema).nullable().optional(),
    channel_breakdown: z.array(ReportsChannelEntrySchema).nullable().optional(),
    period_comparison: ReportsPeriodComparisonSchema.nullable().optional(),
    same_store: z.array(ReportsSameStoreEntrySchema).nullable().optional(),
    category_mix: z.array(ReportsCategoryMixEntrySchema).nullable().optional(),
    category_recommendations: ReportsCategoryRecommendationsSchema.nullable().optional(),
    modifier_mix: z.array(ReportsModifierMixEntrySchema).nullable().optional(),
    combo_mix: z.array(ReportsComboMixEntrySchema).nullable().optional(),
    location_margins: z.array(ReportsLocationMarginEntrySchema).nullable().optional(),
    inventory_insights: z.unknown().optional(),
    labor_insights: z.unknown().optional(),
    refund_activity: z.array(ReportsRefundActivityEntrySchema).nullable().optional(),
    catering: z.unknown().optional(),
  })
  .passthrough();

const ReportSchedulesListSchema = z
  .object({
    schedules: z.array(z.unknown()).optional(),
  })
  .passthrough();

const ReportScheduleEnvelopeSchema = z
  .object({
    schedule: z.unknown().optional(),
    schedules: z.array(z.unknown()).optional(),
    message: z.string().optional(),
  })
  .passthrough();

const IncidentReportEnvelopeSchema = z
  .object({
    range: z.unknown().optional(),
    summary: z.unknown().optional(),
    bySeverity: z.array(z.unknown()).optional(),
    byType: z.array(z.unknown()).optional(),
    incidents: z.array(z.unknown()).optional(),
  })
  .passthrough();

const RewardRedemptionOrderSchema = z
  .object({
    id: z.string(),
    code: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    totalCents: z.number().nullable().optional(),
    currency: z.string().nullable().optional(),
  })
  .passthrough();

const RewardRedemptionRecordSchema = z
  .object({
    id: z.string(),
    token: z.string(),
    type: z.string(),
    consumedAt: z.number(),
    createdAt: z.number(),
    externalId: z.string().nullable().optional(),
    order: RewardRedemptionOrderSchema.nullable(),
  })
  .passthrough();

const RewardRedemptionResponseSchema = z
  .object({
    ok: z.literal(true),
    redemptions: z.array(RewardRedemptionRecordSchema),
  })
  .passthrough();


export interface ReportsResponse {
  range: {
    from: string;
    to: string;
  };
  summary: ReportsSummary;
  sales_by_day: ReportsSalesByDay[];
  top_items: ReportsTopItem[];
  tenders: ReportsTenders;
  z_report: ReportsZSummary;
  sales_totals?: ReportsSalesTotals;
  daypart_breakdown?: ReportsDaypartEntry[];
  channel_breakdown?: ReportsChannelEntry[];
  period_comparison?: ReportsPeriodComparison | null;
  same_store?: ReportsSameStoreEntry[];
  category_mix?: ReportsCategoryMixEntry[];
  category_recommendations?: ReportsCategoryRecommendations | null;
  modifier_mix?: ReportsModifierMixEntry[];
  combo_mix?: ReportsComboMixEntry[];
  location_margins?: ReportsLocationMarginEntry[];
  inventory_insights?: ReportsInventoryInsights;
  labor_insights?: ReportsLaborInsights;
  refund_activity?: ReportsRefundActivityEntry[];
  catering?: ReportsCateringSnapshot;
}

export interface IncidentReportSummary {
  total: number;
  open: number;
  resolved: number;
  mttaMinutes: number | null;
  mttrMinutes: number | null;
}

export interface IncidentReportSeverityEntry {
  severity: string;
  count: number;
}

export interface IncidentReportTypeEntry {
  type: string;
  count: number;
}

export interface IncidentReportIncident {
  id: string;
  severity: string;
  source: string | null;
  description: string | null;
  detectedAt: number;
  resolvedAt: number | null;
  ackAt: number | null;
  qrCodeId: string | null;
  tableId: string | null;
  tableNumber: number | string | null;
  locationId: string | null;
  locationName: string | null;
  zoneName: string | null;
  metadata: Record<string, unknown>;
  category: string | null;
}

export interface IncidentReportResponse {
  range: {
    from: string | null;
    to: string | null;
  };
  summary: IncidentReportSummary;
  bySeverity: IncidentReportSeverityEntry[];
  byType: IncidentReportTypeEntry[];
  incidents: IncidentReportIncident[];
}

export interface IncidentReportFilters {
  from?: string;
  to?: string;
  severity?: string;
  signal?: AbortSignal;
}

export interface RewardRedemptionOrderSnapshot {
  id: string;
  code: string | null;
  status: string | null;
  totalCents: number | null;
  currency: string | null;
}

export interface RewardRedemptionRecord {
  id: string;
  token: string;
  type: string;
  consumedAt: number;
  createdAt: number;
  externalId: string | null;
  order: RewardRedemptionOrderSnapshot | null;
}

export interface RewardRedemptionFilters {
  from?: string;
  to?: string;
  orderId?: string;
  token?: string;
  limit?: number;
}

export type ReportScheduleCadence = 'daily' | 'weekly' | 'monthly';

export type ReportExportTarget =
  | 'reports:sales'
  | 'reports:items'
  | 'reports:incidents'
  | 'reports:closeout'
  | 'orders'
  | 'inventory';

export interface ReportSchedule {
  id: string;
  label: string;
  target: ReportExportTarget;
  format: 'csv' | 'json';
  cadence: ReportScheduleCadence;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  hour: number;
  minute: number;
  recipients: string[];
  filters: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

export interface CreateReportScheduleInput {
  label: string;
  target: ReportExportTarget;
  format: 'csv' | 'json';
  cadence: ReportScheduleCadence;
  dayOfWeek?: number;
  dayOfMonth?: number;
  hour: number;
  minute: number;
  recipients: string[];
  filters?: Record<string, unknown>;
}

export interface FinanceSnapshotRequest {
  from: string;
  to: string;
  title?: string;
  notes?: string;
  signal?: AbortSignal;
}

export interface FinanceSnapshotResult {
  blob: Blob;
  filename: string;
}

export async function getReports(from: string, to: string, signal?: AbortSignal): Promise<ReportsResponse> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const search = params.toString();
  const path = '/reports' + (search ? '?' + search : '');
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(path, {
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 404) {
    return {
      range: { from, to },
      summary: {
        total_revenue_cents: 0,
        orders: 0,
        avg_order_value_cents: 0,
      },
      sales_by_day: [],
      top_items: [],
      tenders: {
        cash_cents: 0,
        card_cents: 0,
        tips_cents: 0,
      },
      z_report: {
        opening_float_cents: 0,
        cash_sales_cents: 0,
        cash_refunds_cents: 0,
        drops_cents: 0,
        payouts_cents: 0,
        closing_cash_cents: 0,
      },
    };
  }

  await assertOk(response);
  return parseJson(response, ReportsResponseSchema);
}

const REPORT_SCHEDULES_PATH = '/reports/schedules';
const REPORT_FINANCE_SNAPSHOT_PATH = '/reports/finance-snapshot';
const DEFAULT_FINANCE_SNAPSHOT_FILENAME = 'finance-snapshot.pdf';

const normalizeScheduleTarget = (value: unknown): ReportExportTarget => {
  if (typeof value === 'string') {
    switch (value) {
      case 'reports:sales':
      case 'reports:items':
      case 'reports:incidents':
      case 'reports:closeout':
      case 'orders':
      case 'inventory':
        return value;
      default:
        break;
    }
  }
  return 'reports:sales';
};

const sanitizeRecipients = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    .map((entry) => entry.trim());
};

const normalizeReportSchedule = (input: unknown): ReportSchedule => {
  const raw = (input ?? {}) as Record<string, unknown>;
  const createdAt = typeof raw.createdAt === 'number' ? raw.createdAt : Date.now();
  const cadenceValue = raw.cadence;
  const cadence: ReportScheduleCadence =
    cadenceValue === 'weekly' || cadenceValue === 'monthly' ? cadenceValue : 'daily';

  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    label: typeof raw.label === 'string' ? raw.label : '',
    target: normalizeScheduleTarget(raw.target),
    format: raw.format === 'json' ? 'json' : 'csv',
    cadence,
    dayOfWeek: typeof raw.dayOfWeek === 'number' ? raw.dayOfWeek : null,
    dayOfMonth: typeof raw.dayOfMonth === 'number' ? raw.dayOfMonth : null,
    hour: typeof raw.hour === 'number' ? raw.hour : 6,
    minute: typeof raw.minute === 'number' ? raw.minute : 0,
    recipients: sanitizeRecipients(raw.recipients),
    filters:
      raw.filters && typeof raw.filters === 'object'
        ? (raw.filters as Record<string, unknown>)
        : {},
    createdAt,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : createdAt,
  };
};

const extractFilenameFromDisposition = (header: string | null): string | null => {
  if (!header) {
    return null;
  }

  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch && utfMatch[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }

  const simpleMatch = header.match(/filename="?([^";]+)"?/i);
  if (simpleMatch && simpleMatch[1]) {
    return simpleMatch[1];
  }

  return null;
};

export async function listReportSchedules(signal?: AbortSignal): Promise<ReportSchedule[]> {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(REPORT_SCHEDULES_PATH, {
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 404) {
    return [];
  }

  await assertOk(response);
  const payload = await parseJson(response, ReportSchedulesListSchema);
  const rawSchedules = Array.isArray(payload.schedules) ? payload.schedules : [];

  return rawSchedules
    .map((entry) => normalizeReportSchedule(entry))
    .filter((schedule) => schedule.id.length > 0);
}

export async function createReportSchedule(
  input: CreateReportScheduleInput,
  signal?: AbortSignal,
): Promise<ReportSchedule> {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(REPORT_SCHEDULES_PATH, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      label: input.label,
      target: input.target,
      format: input.format,
      cadence: input.cadence,
      dayOfWeek: input.dayOfWeek,
      dayOfMonth: input.dayOfMonth,
      hour: input.hour,
      minute: input.minute,
      recipients: input.recipients,
      filters: input.filters ?? {},
    }),
  });

  await assertOk(response);
  const payload = await parseJson(response, ReportScheduleEnvelopeSchema);
  const schedule = normalizeReportSchedule(payload.schedule);
  if (!schedule.id) {
    throw new Error('Schedule response did not include an identifier.');
  }
  return schedule;
}

export async function deleteReportSchedule(
  scheduleId: string,
  signal?: AbortSignal,
): Promise<void> {
  const trimmedId = scheduleId.trim();
  if (!trimmedId) {
    throw new Error('Schedule id is required.');
  }

  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`${REPORT_SCHEDULES_PATH}/${encodeURIComponent(trimmedId)}`, {
    method: 'DELETE',
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 404) {
    throw new Error('Schedule not found.');
  }

  await assertOk(response);
}

export async function downloadFinanceSnapshot(
  params: FinanceSnapshotRequest,
): Promise<FinanceSnapshotResult> {
  const { signal, ...body } = params;
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(REPORT_FINANCE_SNAPSHOT_PATH, {
    method: 'POST',
    signal,
    headers: {
      Accept: 'application/pdf',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      from: body.from,
      to: body.to,
      title: body.title,
      notes: body.notes,
    }),
  });

  if (response.status === 404) {
    throw new Error('Finance snapshots are not available in this environment yet.');
  }

  try {
    await assertOk(response);
  } catch (error) {
    if (error instanceof ApiResponseError && error.body && typeof error.body === 'object') {
      const message = typeof (error.body as { message?: unknown }).message === 'string'
        ? ((error.body as { message?: string }).message as string)
        : `Failed to download finance snapshot (status ${response.status})`;
      throw new Error(message);
    }
    throw error;
  }

  const blob = await response.blob();
  const filename =
    extractFilenameFromDisposition(response.headers.get('Content-Disposition')) ??
    DEFAULT_FINANCE_SNAPSHOT_FILENAME;
  return {
    blob,
    filename,
  };
}

const buildIncidentSearchParams = (filters?: IncidentReportFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters?.from) {
    params.set('from', filters.from);
  }
  if (filters?.to) {
    params.set('to', filters.to);
  }
  if (filters?.severity) {
    params.set('severity', filters.severity);
  }
  return params;
};

export async function getIncidentReport(filters?: IncidentReportFilters): Promise<IncidentReportResponse> {
  const params = buildIncidentSearchParams(filters);
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/reports/incidents${params.size ? `?${params.toString()}` : ''}`, {
    signal: filters?.signal,
    headers: {
      Accept: 'application/json',
    },
  });

  await assertOk(response);
  const payload = await parseJson(response, IncidentReportEnvelopeSchema);
  const incidentsRaw = Array.isArray(payload.incidents) ? payload.incidents : [];

  return {
    range: parseIncidentRange(payload.range),
    summary: parseIncidentSummary(payload.summary),
    bySeverity: Array.isArray(payload.bySeverity)
      ? (payload.bySeverity as IncidentReportSeverityEntry[])
      : [],
    byType: Array.isArray(payload.byType)
      ? (payload.byType as IncidentReportTypeEntry[])
      : [],
    incidents: incidentsRaw as IncidentReportIncident[],
  };
}

export async function downloadIncidentReportCsv(filters?: IncidentReportFilters): Promise<string> {
  const params = buildIncidentSearchParams(filters);
  return requestCsv(`/reports/incidents/export${params.size ? `?${params.toString()}` : ''}`, {
    signal: filters?.signal,
  });
}

export async function downloadIncidentReportJson(filters?: IncidentReportFilters): Promise<string> {
  const report = await getIncidentReport(filters);
  return JSON.stringify(report, null, 2);
}

export const fetchRewardRedemptions = async (
  filters: RewardRedemptionFilters = {},
  signal?: AbortSignal,
): Promise<RewardRedemptionRecord[]> => {
  const params = new URLSearchParams();
  if (filters.from) {
    params.set('from', filters.from);
  }
  if (filters.to) {
    params.set('to', filters.to);
  }
  if (filters.orderId) {
    params.set('orderId', filters.orderId);
  }
  if (filters.token) {
    params.set('token', filters.token);
  }
  if (typeof filters.limit === 'number') {
    params.set('limit', String(filters.limit));
  }

  const queryString = params.toString();
  const path = queryString.length > 0 ? `/reports/rewards/redemptions?${queryString}` : '/reports/rewards/redemptions';

  const response = await requestJson<z.infer<typeof RewardRedemptionResponseSchema>>(path, {
    signal,
    schema: RewardRedemptionResponseSchema,
  });

  return response.redemptions.map((entry) => ({
    id: entry.id,
    token: entry.token,
    type: entry.type,
    consumedAt: entry.consumedAt,
    createdAt: entry.createdAt,
    externalId: entry.externalId ?? null,
    order: entry.order
      ? {
          id: entry.order.id,
          code: entry.order.code ?? null,
          status: entry.order.status ?? null,
          totalCents: entry.order.totalCents ?? null,
          currency: entry.order.currency ?? null,
        }
      : null,
  }));
};

const defaultIncidentSummary: IncidentReportSummary = {
  total: 0,
  open: 0,
  resolved: 0,
  mttaMinutes: null,
  mttrMinutes: null,
};

const parseIncidentRange = (value: unknown): IncidentReportResponse['range'] => {
  if (!value || typeof value !== 'object') {
    return { from: null, to: null };
  }
  const record = value as Record<string, unknown>;
  return {
    from: typeof record.from === 'string' ? record.from : null,
    to: typeof record.to === 'string' ? record.to : null,
  };
};

const parseIncidentSummary = (value: unknown): IncidentReportSummary => {
  if (!value || typeof value !== 'object') {
    return defaultIncidentSummary;
  }
  const record = value as Record<string, unknown>;
  const readNumber = (input: unknown): number => (typeof input === 'number' ? input : 0);
  const readNullableNumber = (input: unknown): number | null => (typeof input === 'number' ? input : null);

  return {
    total: readNumber(record.total),
    open: readNumber(record.open),
    resolved: readNumber(record.resolved),
    mttaMinutes: readNullableNumber(record.mttaMinutes),
    mttrMinutes: readNullableNumber(record.mttrMinutes),
  };
};




type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type ReportFormat = 'json' | 'csv';

const DEFAULT_REQUEST_TIMEOUT_MS = 12_000;

const isDomException = (value: unknown): value is DOMException =>
  typeof DOMException !== 'undefined' && value instanceof DOMException;

const createTimeoutController = (
  upstream?: AbortSignal,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
) => {
  if (typeof AbortController === 'undefined') {
    return { signal: upstream, cleanup: () => {}, timeoutReason: null as Error | null };
  }

  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutReason: Error | null = timeoutMs > 0
    ? (typeof DOMException === 'function'
        ? new DOMException('Request timed out', 'TimeoutError')
        : Object.assign(new Error('Request timed out'), { name: 'TimeoutError' }))
    : null;

  if (timeoutMs > 0 && timeoutReason) {
    timeoutId = setTimeout(() => {
      if (!controller.signal.aborted) {
        controller.abort(timeoutReason);
      }
    }, timeoutMs);
  }

  const abortFromUpstream = () => {
    if (!controller.signal.aborted) {
      controller.abort();
    }
  };

  if (upstream) {
    if (upstream.aborted) {
      abortFromUpstream();
    } else {
      upstream.addEventListener('abort', abortFromUpstream);
    }
  }

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (upstream) {
      upstream.removeEventListener('abort', abortFromUpstream);
    }
  };

  controller.signal.addEventListener('abort', cleanup, { once: true });

  return { signal: controller.signal, cleanup, timeoutReason } as const;
};

async function request(
  path: string,
  {
    method = 'GET',
    format = 'json',
    signal,
    body,
    headers,
  }: { method?: HttpMethod; format?: ReportFormat; signal?: AbortSignal; body?: unknown; headers?: Record<string, string> } = {},
): Promise<Response> {
  const { signal: combinedSignal, cleanup, timeoutReason } = createTimeoutController(signal);

  try {
    const apiFetch = await resolveApiFetch();
    const response = await apiFetch(path, {
      method,
      headers: {
        Accept: format === 'csv' ? 'text/csv' : 'application/json',
        ...headers,
      },
      signal: combinedSignal,
      body: body !== undefined ? (typeof body === 'string' || body instanceof FormData ? (body as BodyInit) : JSON.stringify(body)) : undefined,
    });

    await assertOk(response);
    return response;
  } catch (error) {
    const abortReason =
      combinedSignal && 'reason' in combinedSignal
        ? (combinedSignal as AbortSignal & { reason?: unknown }).reason
        : undefined;

    const abortedDueToTimeout =
      timeoutReason && combinedSignal?.aborted && abortReason === timeoutReason;

    if (abortedDueToTimeout) {
      throw new Error('Reports request timed out. Please try again.');
    }

    if (isDomException(error) && error.name === 'AbortError') {
      throw error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    throw error;
  } finally {
    cleanup();
  }
}
async function requestJson<T>(
  path: string,
  options: { method?: HttpMethod; signal?: AbortSignal; schema?: z.ZodType<T>; body?: unknown; headers?: Record<string, string> } = {},
): Promise<T> {
  const { schema, ...rest } = options;
  const response = await request(path, { ...rest, format: 'json' });

  if (response.status === 204) {
    const targetSchema = schema ?? (z.unknown() as z.ZodType<T>);
    const parsed = targetSchema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiResponseError('Expected JSON response', response.status);
    }
    return parsed.data;
  }

  return parseJson(response, schema ?? (z.unknown() as z.ZodType<T>));
}

async function requestCsv(
  path: string,
  options: { method?: HttpMethod; signal?: AbortSignal; headers?: Record<string, string> } = {},
): Promise<string> {
  const response = await request(path, { ...options, format: 'csv' });
  return response.text();
}

interface RawSalesSeries {
  date: string;
  revenue_cents: number;
  orders: number;
}

interface RawSalesResponse {
  ok: boolean;
  tz: string;
  range: { from?: string; to?: string } | null;
  totals_cents: number;
  orders_count: number;
  avg_order_value_cents: number;
  series?: RawSalesSeries[];
}
const RawSalesResponseSchema = z
  .object({
    ok: z.boolean(),
    tz: z.string(),
    range: z
      .object({
        from: z.string().optional(),
        to: z.string().optional(),
      })
      .nullable()
      .optional(),
    totals_cents: z.number(),
    orders_count: z.number(),
    avg_order_value_cents: z.number(),
    series: z
      .array(
        z
          .object({
            date: z.string(),
            revenue_cents: z.number(),
            orders: z.number(),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();


export interface SalesSeriesPoint {
  date: string;
  revenueCents: number;
  orders: number;
}

export interface SalesReport {
  tz: string;
  range: { from: string; to: string };
  totalsCents: number;
  ordersCount: number;
  averageOrderValueCents: number;
  series: SalesSeriesPoint[];
}

const mapSalesResponse = (raw: RawSalesResponse): SalesReport => {
  const series = Array.isArray(raw.series)
    ? raw.series.map((entry) => ({
        date: entry.date,
        revenueCents: Number(entry.revenue_cents) || 0,
        orders: Number(entry.orders) || 0,
      }))
    : [];

  return {
    tz: raw.tz ?? 'UTC',
    range: {
      from: raw.range?.from ?? '',
      to: raw.range?.to ?? '',
    },
    totalsCents: Number(raw.totals_cents) || 0,
    ordersCount: Number(raw.orders_count) || 0,
    averageOrderValueCents: Number(raw.avg_order_value_cents) || 0,
    series,
  };
};

export interface SalesQueryParams {
  from: string;
  to: string;
}

export const getSales = async (params: SalesQueryParams, signal?: AbortSignal): Promise<SalesReport> => {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  const raw = await requestJson<RawSalesResponse>('/reports/sales?' + query.toString(), {
    signal,
    schema: RawSalesResponseSchema,
  });
  if (!raw.ok) {
    throw new Error('Sales report unavailable');
  }
  return mapSalesResponse(raw);
};

export const getSalesCsv = async (params: SalesQueryParams, signal?: AbortSignal): Promise<string> => {
  const query = new URLSearchParams({ from: params.from, to: params.to, format: 'csv' });
  return requestCsv('/reports/sales?' + query.toString(), { signal });
};

interface RawItemsRow {
  item_id?: string;
  name?: string;
  qty?: number;
  revenue_cents?: number;
}

interface RawItemsResponse {
  ok: boolean;
  tz: string;
  range: { from?: string; to?: string } | null;
  top_by_qty?: RawItemsRow[];
  top_by_revenue?: RawItemsRow[];
}
const RawItemsResponseSchema = z
  .object({
    ok: z.boolean(),
    tz: z.string(),
    range: z
      .object({
        from: z.string().optional(),
        to: z.string().optional(),
      })
      .nullable()
      .optional(),
    top_by_qty: z
      .array(
        z
          .object({
            item_id: z.string().optional(),
            name: z.string().optional(),
            qty: z.number().optional(),
            revenue_cents: z.number().optional(),
          })
          .passthrough(),
      )
      .optional(),
    top_by_revenue: z
      .array(
        z
          .object({
            item_id: z.string().optional(),
            name: z.string().optional(),
            qty: z.number().optional(),
            revenue_cents: z.number().optional(),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();


export interface RankedItem {
  itemId: string;
  name: string;
  qty: number;
  revenueCents: number;
}

export interface ItemsReport {
  tz: string;
  range: { from: string; to: string };
  topByQty: RankedItem[];
  topByRevenue: RankedItem[];
}

const mapItemsRows = (rows: RawItemsRow[] | undefined): RankedItem[] => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => ({
    itemId: row.item_id ?? '',
    name: row.name ?? 'Unknown Item',
    qty: Number(row.qty) || 0,
    revenueCents: Number(row.revenue_cents) || 0,
  }));
};

const mapItemsResponse = (raw: RawItemsResponse): ItemsReport => ({
  tz: raw.tz ?? 'UTC',
  range: {
    from: raw.range?.from ?? '',
    to: raw.range?.to ?? '',
  },
  topByQty: mapItemsRows(raw.top_by_qty),
  topByRevenue: mapItemsRows(raw.top_by_revenue),
});

export const getItems = async (params: SalesQueryParams, signal?: AbortSignal): Promise<ItemsReport> => {
  try {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    const raw = await requestJson<RawItemsResponse>('/reports/items?' + query.toString(), {
      signal,
      schema: RawItemsResponseSchema,
    });
    if (!raw.ok) {
      throw new Error('Items report unavailable');
    }
    return mapItemsResponse(raw);
  } catch (error) {
    console.error('[getItems] API call failed, returning empty items report:', error);
    // Return empty items report when API fails to allow dashboard to load
    return {
      tz: 'UTC',
      range: { from: params.from, to: params.to },
      topByQty: [],
      topByRevenue: [],
    };
  }
};

export const getItemsCsv = async (params: SalesQueryParams, signal?: AbortSignal): Promise<string> => {
  const query = new URLSearchParams({ from: params.from, to: params.to, format: 'csv' });
  return requestCsv('/reports/items?' + query.toString(), { signal });
};

interface RawZResponse {
  ok: boolean;
  tz: string;
  date: string;
  payments?: {
    cash_cents?: number;
    card_cents?: number;
    tips_cents?: number;
    refunds_cents?: number;
  } | null;
  orders?: {
    count?: number;
    completed_count?: number;
    canceled_count?: number;
    void_count?: number;
  } | null;
  tax_cents?: number;
  total_cents?: number;
}
const RawZResponseSchema = z
  .object({
    ok: z.boolean(),
    tz: z.string(),
    date: z.string(),
    payments: z
      .object({
        cash_cents: z.number().optional(),
        card_cents: z.number().optional(),
        tips_cents: z.number().optional(),
        refunds_cents: z.number().optional(),
      })
      .nullable()
      .optional(),
    orders: z
      .object({
        count: z.number().optional(),
        completed_count: z.number().optional(),
        canceled_count: z.number().optional(),
        void_count: z.number().optional(),
      })
      .nullable()
      .optional(),
    tax_cents: z.number().optional(),
    total_cents: z.number().optional(),
  })
  .passthrough();


export interface ZReport {
  tz: string;
  date: string;
  payments: {
    cashCents: number;
    cardCents: number;
    tipsCents: number;
    refundsCents: number;
  };
  orders: {
    count: number;
    completedCount: number;
    canceledCount: number;
    voidCount: number;
  };
  taxCents: number;
  totalCents: number;
}

export interface ZReportParams {
  date: string;
}

const mapZResponse = (raw: RawZResponse): ZReport => ({
  tz: raw.tz ?? 'UTC',
  date: raw.date ?? '',
  payments: {
    cashCents: Number(raw.payments?.cash_cents) || 0,
    cardCents: Number(raw.payments?.card_cents) || 0,
    tipsCents: Number(raw.payments?.tips_cents) || 0,
    refundsCents: Number(raw.payments?.refunds_cents) || 0,
  },
  orders: {
    count: Number(raw.orders?.count) || 0,
    completedCount: Number(raw.orders?.completed_count) || 0,
    canceledCount: Number(raw.orders?.canceled_count) || 0,
    voidCount: Number(raw.orders?.void_count) || 0,
  },
  taxCents: Number(raw.tax_cents) || 0,
  totalCents: Number(raw.total_cents) || 0,
});

export const getZ = async (params: ZReportParams, signal?: AbortSignal): Promise<ZReport> => {
  const query = new URLSearchParams({ date: params.date });
  const raw = await requestJson<RawZResponse>('/reports/z?' + query.toString(), {
    signal,
    schema: RawZResponseSchema,
  });
  if (!raw.ok) {
    throw new Error('Z-report unavailable');
  }
  return mapZResponse(raw);
};

export const getZCsv = async (params: ZReportParams, signal?: AbortSignal): Promise<string> => {
  const query = new URLSearchParams({ date: params.date, format: 'csv' });
  return requestCsv('/reports/z?' + query.toString(), { signal });
};

// ---------------------------------------------------------------------------
// Legacy compatibility wrappers (deprecated)
// ---------------------------------------------------------------------------

export interface SalesKpi {
  date: string;
  totalOrders: number;
  revenueCents: number;
  averageOrderCents?: number;
}

export interface SalesReportResponse {
  summary: {
    totalOrders: number;
    revenueCents: number;
    averageOrderCents: number;
  };
  series?: SalesKpi[];
}

export interface SalesReportParams {
  from: string;
  to: string;
}

export async function fetchSalesReport(params: SalesReportParams, signal?: AbortSignal): Promise<SalesReportResponse> {
  const report = await getSales(params, signal);
  const series = report.series.map((row) => ({
    date: row.date,
    totalOrders: row.orders,
    revenueCents: row.revenueCents,
    averageOrderCents: row.orders === 0 ? 0 : Math.round(row.revenueCents / row.orders),
  }));
  return {
    summary: {
      totalOrders: report.ordersCount,
      revenueCents: report.totalsCents,
      averageOrderCents: report.averageOrderValueCents,
    },
    series,
  };
}

export async function downloadSalesReportCsv(params: SalesReportParams, signal?: AbortSignal): Promise<string> {
  return getSalesCsv(params, signal);
}

export async function downloadSalesReportJson(params: SalesReportParams, signal?: AbortSignal): Promise<string> {
  const report = await fetchSalesReport(params, signal);
  return JSON.stringify(report, null, 2);
}

export interface ItemSalesRow {
  itemId: string;
  name: string;
  quantity: number;
  revenueCents: number;
}

export interface ItemsReportResponse {
  rows: ItemSalesRow[];
}

export async function fetchItemsReport(params: SalesReportParams, signal?: AbortSignal): Promise<ItemsReportResponse> {
  try {
    const report = await getItems(params, signal);
    return {
      rows: report.topByQty.map((row) => ({
        itemId: row.itemId,
        name: row.name,
        quantity: row.qty,
        revenueCents: row.revenueCents,
      })),
    };
  } catch (error) {
    console.error('[fetchItemsReport] API call failed, returning empty items report:', error);
    // Return empty items report when API fails to allow dashboard to load
    return { rows: [] };
  }
}

export async function downloadItemsReportCsv(params: SalesReportParams, signal?: AbortSignal): Promise<string> {
  return getItemsCsv(params, signal);
}

export async function downloadItemsReportJson(params: SalesReportParams, signal?: AbortSignal): Promise<string> {
  const report = await fetchItemsReport(params, signal);
  return JSON.stringify(report, null, 2);
}

export interface ZReportLegacyResponse {
  range: {
    from: string;
    to: string;
    timezone: string;
  };
  orders: {
    total: number;
    completed: number;
    open: number;
    grossSalesCents: number;
    netSalesCents: number;
    taxCents: number;
    serviceCents: number;
    subtotalCents: number;
  };
  payments: {
    count: number;
    totalCents: number;
    cashCents: number;
    cardCents: number;
    otherCents: number;
    tipCents: number;
  };
}

export async function fetchZReport(params: SalesReportParams, signal?: AbortSignal): Promise<ZReportLegacyResponse> {
  const date = params.from;
  const report = await getZ({ date }, signal);
  const totalCents = report.payments.cashCents + report.payments.cardCents;
  return {
    range: {
      from: date,
      to: date,
      timezone: report.tz,
    },
    orders: {
      total: report.orders.count,
      completed: report.orders.completedCount,
      open: Math.max(report.orders.count - report.orders.completedCount - report.orders.canceledCount - report.orders.voidCount, 0),
      grossSalesCents: report.totalCents,
      netSalesCents: report.totalCents - report.taxCents,
      taxCents: report.taxCents,
      serviceCents: 0,
      subtotalCents: report.totalCents - report.taxCents,
    },
    payments: {
      count: report.orders.count,
      totalCents,
      cashCents: report.payments.cashCents,
      cardCents: report.payments.cardCents,
      otherCents: 0,
      tipCents: report.payments.tipsCents,
    },
  };
}

export async function downloadZReportCsv(params: SalesReportParams, signal?: AbortSignal): Promise<string> {
  const date = params.from;
  return getZCsv({ date }, signal);
}
